import React from 'react';
import {GridContainer} from '../../elements/grid/GridContainer';
import {EyebrowProps} from '../../elements/eyebrow/Eyebrow';
import {ImageProps} from '../../elements/images/Image';
import {BeveledImage} from '../../elements/images/BeveledImage';
import {VerticalSpacer} from '../../elements/spacer/VerticalSpacer';
import {AccentColorOptions, useBreakpointOnly} from 'epic-ue-theme';
import {Typography} from '../../elements/typography/Typography';
import {Button} from '../../elements/buttons/Button';
import {AnimateOnViewWrap} from '../../wraps/animation';

import styled from 'styled-components';
import {useIsTwinmotionTheme} from 'epic-ue-theme/src/shared/theme.hooks';

const PluginsSectionWrapper = styled.div`
    &.twinmotion {
        .outline {
            background: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.common.white, p.theme.palette.common.black)};
            border: none;
            .beveled-image {
                background-color: ${(p) => p.theme.ifLight(p, '#F7F7F7', '#313036')};
            }
        }
    }
`;

const getRadialGradient = (from, to) =>
    `radial-gradient(100% 185.14% at 100% 100%, ${from} 0%, ${to} 100%)`;

const PluginsOutlineWrapper = styled.div`
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    text-align: center;
    border: ${(p) => p.theme.spacing(0.5)}px solid;
    .paragraph {
        font-weight: 300;
    }
    h4 {
        color: ${(p) => p.theme.palette.text.secondary};
    }
    ${(p) => p.theme.breakpoints.up('xs')} {
        padding: 0 ${(p) => p.theme.spacing(3)}px;
    }
    ${(p) => p.theme.breakpoints.up('md')} {
        padding: 0;
        h2,
        .paragraph {
            color: ${(p) => p.theme.palette.text.secondary};
            width: 50%;
        }
    }
    background-size: 100%;
    position: relative;
    ${(p) => {
        const colors: string[] = [];
        const {gradient} = p.theme.palette;
        for (const color in gradient) {
            if (Object.prototype.hasOwnProperty.call(gradient, color)) {
                colors.push(`
                    &.${color} {
                        border-image: ${getRadialGradient(
                            gradient[color].from,
                            gradient[color].to
                        )} 5;
                        border-image-width: 4px;
                    }
                `);
            }
        }
        return colors.join('\n');
    }}
`;
export interface PluginsSectionProps {
    accentColor?: AccentColorOptions;
    className?: string;
    ctaText?: string;
    description?: string;
    eyebrow?: EyebrowProps;
    headline?: string;
    href?: string;
    images?: [ImageProps, ImageProps, ImageProps, ImageProps, ImageProps, ImageProps];
}

export const PluginsSection = (props: PluginsSectionProps): JSX.Element => {
    const isTwinmotion = useIsTwinmotionTheme();
    const {
        eyebrow = '',
        headline = '',
        description = '',
        ctaText = 'Browse all plugins',
        href = '',
        className = '',
        images = [],
        accentColor = 'sky'
    } = props;

    let iconPositions = [
        {
            bottom: 215,
            left: 52
        },
        {
            bottom: 55,
            left: -28
        },
        {
            bottom: -28,
            left: 132
        },
        {
            top: -28,
            right: 132
        },
        {
            top: 51,
            right: -28
        },
        {
            top: 211,
            right: 52
        }
    ];

    const isXs = useBreakpointOnly('xs');
    const isSm = useBreakpointOnly('sm');
    const isMd = useBreakpointOnly('md');

    if (isXs) {
        iconPositions = [
            {
                bottom: -24,
                left: -12
            },
            {
                bottom: -136,
                left: 36
            },
            {
                bottom: -72,
                left: 148
            },
            {
                top: -72,
                right: 148
            },
            {
                top: -136,
                right: 36
            },
            {
                top: -24,
                right: -12
            }
        ];
    } else if (isSm) {
        iconPositions = [
            {
                bottom: -24,
                left: -12
            },
            {
                bottom: -136,
                left: 36
            },
            {
                bottom: -72,
                left: 148
            },
            {
                top: -72,
                right: 148
            },
            {
                top: -136,
                right: 36
            },
            {
                top: -24,
                right: -12
            }
        ];
    } else if (isMd) {
        iconPositions = [
            {
                bottom: 181,
                left: 47
            },
            {
                bottom: 51,
                left: -20
            },
            {
                bottom: -20,
                left: 110
            },
            {
                top: -20,
                right: 110
            },
            {
                top: 46,
                right: -20
            },
            {
                top: 176,
                right: 46
            }
        ];
    }

    const icons = (images as ImageProps[]).map((image, index) => {
        return (
            <BeveledImage
                key={`plugin-icon-${index}`}
                image={{
                    src: image.src,
                    alt: image.alt ? image.alt : `Plugin Icon ${index + 1}`
                }}
                top={iconPositions[index].top && iconPositions[index].top}
                right={iconPositions[index].right && iconPositions[index].right}
                bottom={iconPositions[index].bottom && iconPositions[index].bottom}
                left={iconPositions[index].left && iconPositions[index].left}
            />
        );
    });

    return (
        <PluginsSectionWrapper className={`plugins ${isTwinmotion ? 'twinmotion' : ''}`}>
            <VerticalSpacer variant="custom" spacing={12} downSmSpacing={23.5} />
            <AnimateOnViewWrap translateY={30}>
                <GridContainer>
                    <PluginsOutlineWrapper
                        className={`outline ${[className, accentColor].join(' ')}`}>
                        <VerticalSpacer variant="custom" spacing={9} downSmSpacing={12} />
                        <Typography variant="h4" big>
                            {eyebrow && eyebrow.text}
                        </Typography>
                        <VerticalSpacer variant="custom" spacing={4} />
                        <Typography variant="h2">{headline}</Typography>
                        <VerticalSpacer variant="eyebrow" />
                        <Typography variant="p">{description}</Typography>
                        <VerticalSpacer variant="custom" spacing={4} />
                        <Button type="primary" color={accentColor} href={href}>
                            {ctaText}
                        </Button>
                        <VerticalSpacer variant="custom" spacing={9} downSmSpacing={12} />
                        {icons}
                    </PluginsOutlineWrapper>
                </GridContainer>
            </AnimateOnViewWrap>
            <VerticalSpacer variant="custom" spacing={12} downSmSpacing={23.5} />
        </PluginsSectionWrapper>
    );
};
